import {
  RECAPTCHA_TOKEN_ATTEMPT_TIMEOUTS_MS,
  RECAPTCHA_TOKEN_MAX_ATTEMPTS,
  RECAPTCHA_UNAVAILABLE_RETRY_DELAY_MS
} from './constants'

/**
 * Thrown when the reCAPTCHA library isn't on the page yet — either the script is
 * still loading or it was blocked outright. Distinguished from other failures
 * because it's the one case where waiting before a retry can help: nothing about
 * the page has changed, so an immediate retry just re-reads the same missing
 * global.
 */
export class ReCaptchaUnavailableError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'ReCaptchaUnavailableError'
  }
}

export type ReCaptchaFailureReporter = (
  lastError: Error | undefined,
  durationMs: number
) => void

const delay = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms))

/**
 * Runs the attempt loop for reCAPTCHA token generation, resolving with the token
 * or with `undefined` once every attempt has failed. Never rejects —
 * `reportFailure` is invoked with the last error instead.
 *
 * Each attempt is given a progressively longer budget from
 * RECAPTCHA_TOKEN_ATTEMPT_TIMEOUTS_MS. When the library isn't on the page yet
 * (`ReCaptchaUnavailableError`), backs off by RECAPTCHA_UNAVAILABLE_RETRY_DELAY_MS
 * before retrying rather than burning all attempts synchronously.
 */
export const generateReCaptchaTokenWithRetries = async (
  siteKey: string,
  action: string,
  reportFailure: ReCaptchaFailureReporter
): Promise<string | undefined> => {
  const startedAt = Date.now()
  let lastError: Error | undefined

  for (const [
    attemptIndex,
    timeoutMs
  ] of RECAPTCHA_TOKEN_ATTEMPT_TIMEOUTS_MS.entries()) {
    try {
      return await generateReCaptchaToken(siteKey, action, timeoutMs)
    } catch (e) {
      lastError = e instanceof Error ? e : new Error(String(e))

      if (
        e instanceof ReCaptchaUnavailableError &&
        attemptIndex < RECAPTCHA_TOKEN_MAX_ATTEMPTS - 1
      ) {
        await delay(RECAPTCHA_UNAVAILABLE_RETRY_DELAY_MS)
      }
    }
  }

  reportFailure(lastError, Date.now() - startedAt)
  return undefined
}

/**
 * Requests a reCAPTCHA Enterprise token for the given site key and action.
 *
 * Rejects rather than returning a falsy token, so callers only have to handle
 * one failure path. Rejects when:
 *  - the reCAPTCHA library isn't loaded yet (`grecaptcha` absent) —
 *    `ReCaptchaUnavailableError`, so callers can retry this case differently
 *  - `ready` never invokes its callback within `timeoutMs`
 *  - `execute` itself fails
 *  - reCAPTCHA resolves with an empty token
 *
 * `timeoutMs` is a parameter rather than a constant because a retrying caller
 * gives each attempt a different budget — see
 * RECAPTCHA_TOKEN_ATTEMPT_TIMEOUTS_MS.
 *
 * Note that both callers of this in sites-web pass different site keys — the
 * sitewide `reCaptchaEnterprise` key and the separate `giftCardRecaptchaSiteKey`
 * — so the key is always an explicit argument.
 */
export const generateReCaptchaToken = async (
  siteKey: string,
  action: string,
  timeoutMs: number
): Promise<string> => {
  if (typeof grecaptcha === 'undefined' || !grecaptcha.enterprise) {
    throw new ReCaptchaUnavailableError('grecaptcha.enterprise is unavailable')
  }

  const token = await new Promise<string>((resolve, reject) => {
    const timeoutId = setTimeout(
      () =>
        reject(
          new Error(`reCAPTCHA token generation timed out after ${timeoutMs}ms`)
        ),
      timeoutMs
    )

    grecaptcha.enterprise.ready(async () => {
      try {
        resolve(await grecaptcha.enterprise.execute(siteKey, { action }))
      } catch (e) {
        reject(e)
      } finally {
        clearTimeout(timeoutId)
      }
    })
  })

  if (!token) {
    throw new Error('reCAPTCHA returned an empty token')
  }

  return token
}
